<template>
  <v-dialog
    :value="enableDialog"
    persistent
    width="980"
    @keydown.esc="closeButtonClickEvent"
    @close="closeButtonClickEvent"
  >
    <validation-observer ref="observer">
      <v-card>
        <v-toolbar flat>
          <v-toolbar-title>請求先追加</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text
          v-if="billing_address"
          style="max-height: 65vh;overflow-y: auto;"
        >
          <v-row dense>
            <v-col class="text-right">
              <v-btn
                class="primary"
                @click="copyFromEmissionButtonClickEvent"
              >
                排出事業者情報をコピー
              </v-btn>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true, alpha_num_only: true, min: 4, max: 10 }"
                class="requiredMark"
                name="請求先コード"
              >
                <v-text-field
                  v-model="billing_address.code"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="請求先コード"
                  outlined
                  persistent-placeholder
                  placeholder="例）B001"
                >
                  <template v-slot:label>
                    <span>請求先コード</span>
                    <span class="ml-1">
                      ({{ billing_address.code?.length || 0 }} / 10)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="8">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="請求先名"
                rules="required|max:60"
              >
                <v-text-field
                  v-model="billing_address.name"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="請求先名"
                  outlined
                  persistent-placeholder
                  placeholder="例）△△△△株式会社"
                >
                  <template v-slot:label>
                    <span>請求先名</span>
                    <span class="ml-1">
                      ({{ billing_address.name?.length || 0 }} / 60)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>

          <!-- 郵便番号 -->
          <v-row dense>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                :rules="{zip_code: true, required: true, max:8 }"
                name="郵便番号"
                class="requiredMark"
              >
                <v-text-field
                  v-model="billing_address.zip_code"
                  :error-messages="errors"
                  dense
                  label="郵便番号"
                  hide-details="auto"
                  hint="ハイフンを含めて入力してください"
                  outlined
                  persistent-hint
                  persistent-placeholder
                  placeholder="例）111-1111"
                  required
                >
                  <template v-slot:label>
                    <span>郵便番号</span>
                    <span class="ml-1">
                      ({{ billing_address.zip_code?.length || 0 }} / 8)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                  <template v-slot:append-outer>
                    <zip-code-search
                      :zip-code="billing_address.zip_code"
                      @zipAddress="setZipAddress"
                    />
                    &nbsp;
                    <v-btn
                      outlined
                      small
                      @click="searchZipButtonClickEvent"
                    >
                      住所→郵便番号
                    </v-btn>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /郵便番号 -->

          <!-- 都道府県／市区町村 -->
          <v-row dense>
            <v-col cols="12" md="3">
              <validation-provider
                v-slot="{ errors }"
                name="都道府県"
                rules="required"
                class="requiredMark"
              >
                <v-autocomplete
                  v-model="billing_address.prefecture_code"
                  :error-messages="errors"
                  :items="prefList"
                  dense
                  hide-details="auto"
                  item-text="text"
                  item-value="value"
                  label="都道府県"
                  outlined
                  persistent-placeholder
                  placeholder="例）東京都"
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="9">
              <validation-provider
                v-slot="{ errors }"
                name="市区町村"
                rules="required|full_width|max:20"
                class="requiredMark"
              >
                <v-text-field
                  v-model="billing_address.address_1"
                  :error-messages="errors"
                  dense
                  label="市区町村"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）中央区"
                >
                  <template v-slot:label>
                    <span>市区町村</span>
                    <span class="ml-1">
                      ({{ billing_address.address_1?.length || 0 }} / 20)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /都道府県／市区町村 -->

          <!-- 町域 -->
          <v-row dense>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="町域"
                rules="full_width|max:40"
              >
                <v-text-field
                  v-model="billing_address.address_2"
                  :error-messages="errors"
                  dense
                  label="町域"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）日本橋兜町"
                >
                  <template v-slot:label>
                    <span>町域</span>
                    <span class="ml-1">
                      ({{ billing_address.address_2?.length || 0 }} / 40)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
            <!-- /町域 -->
            <!-- 詳細住所 -->
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="詳細住所"
                rules="max:50"
              >
                <v-text-field
                  v-model="billing_address.address_3"
                  :error-messages="errors"
                  dense
                  label="詳細住所"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）１－２－３"
                >
                  <template v-slot:label>
                    <span>詳細住所</span>
                    <span class="ml-1">
                      ({{ billing_address.address_3?.length || 0 }} / 50)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /詳細住所 -->

          <!-- 電話番号 -->
          <v-row dense>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="電話番号"
                :rules="{ numeric_hyphen_only: true,min: 2, max: 15 }"
              >
                <v-text-field
                  v-model="billing_address.phone_number"
                  :error-messages="errors"
                  dense
                  hint="電話番号を「-」ハイフン「( )」込みで入力します"
                  persistent-hint
                  label="電話番号"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）000-0000-0000"
                >
                  <template v-slot:label>
                    <span>電話番号</span>
                    <span class="ml-1">
                      ({{ billing_address.phone_number?.length || 0 }} / 15)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /電話番号 -->

          <!-- 登録番号・メモ欄 -->
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ alpha_num_only: true, length: 14 }"
                name="登録番号（適格請求書発行事業者）"
              >
                <v-text-field
                  v-model="billing_address.register_number"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="登録番号（適格請求書発行事業者）"
                  outlined
                  persistent-placeholder
                  placeholder="例）T1234567890123"
                >
                  <template v-slot:label>
                    <span>登録番号（適格請求書発行事業者）</span>
                    <span class="ml-1">
                      ({{ billing_address.register_number?.length || 0 }} / 14)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="メモ欄"
                rules="max:30"
              >
                <v-text-field
                  v-model="billing_address.memo"
                  :error-messages="errors"
                  dense
                  label="メモ欄"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder=""
                >
                  <template v-slot:label>
                    <span>メモ欄</span>
                    <span class="ml-1">
                      ({{ billing_address.memo?.length || 0 }} / 30)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /登録番号・メモ欄 -->

          <v-divider />

          <h2 class="ma-3 font-weight-thin" style="color: #0074BA">締め日等</h2>

          <!-- 会社内で請求先を共有 -->
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="billing_address.use_share"
                label="会社内で請求先を共有する"
                hide-details="auto"
                class="pa-0 ma-0 mb-4"
              />
            </v-col>
          </v-row>
          <!-- /会社内で請求先を共有-->

          <!-- 締め日/支払いサイト -->
          <v-row dense>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="締め日"
                rules="required"
              >
                <v-select
                  v-model="billing_address.close_day_type"
                  :items="closeDayTypes"
                  :error-messages="errors"
                  dense
                  label="締め日"
                  outlined
                  persistent-placeholder
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="支払いサイト"
                rules="required"
              >
                <v-select
                  v-model="billing_address.payment_site"
                  :items="paymentSites"
                  :error-messages="errors"
                  dense
                  label="支払いサイト"
                  outlined
                  persistent-placeholder
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /締め日/支払いサイト -->

          <!-- 入金日/入金方法 -->
          <v-row dense>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="入金日"
                rules="required"
              >
                <v-select
                  v-model="billing_address.payment_day_type"
                  :items="paymentDayTypes"
                  :error-messages="errors"
                  dense
                  label="入金日"
                  outlined
                  persistent-placeholder
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="入金方法"
                rules="required"
              >
                <v-select
                  v-model="billing_address.payment_method_type"
                  :items="paymentMethodTypes"
                  :error-messages="errors"
                  dense
                  label="入金方法"
                  outlined
                  persistent-placeholder
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /入金日/入金方法 -->

          <!-- 相殺区分/端数処理区分 -->
          <v-row dense>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="相殺区分"
                rules="required"
              >
                <v-select
                  v-model="billing_address.counterbalance_type"
                  :items="counterbalanceTypes"
                  :error-messages="errors"
                  dense
                  label="相殺区分"
                  outlined
                  persistent-placeholder
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="端数処理区分"
                rules="required"
              >
                <v-select
                  v-model="billing_address.fraction_type"
                  :items="fractionTypes"
                  :error-messages="errors"
                  dense
                  label="端数処理区分"
                  outlined
                  persistent-placeholder
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /相殺区分/端数処理区分 -->

        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="closeButtonClickEvent"
          >
            閉じる
          </v-btn>
          <v-btn
            class="primary"
            @click="registerButtonClickEvent"
          >
            登録
          </v-btn>
        </v-card-actions>
      </v-card>
    </validation-observer>
    <search-zip-modal
      :enable="enableSearchZipModal"
      :initial-prefecture-code="billing_address.prefecture_code"
      :initial-city-name="billing_address.address_1"
      :initial-town-name="billing_address.address_2"
      @reflectFullCallback="searchZipReflectCallback"
      @reflectZipCallback="searchZipReflectZipCallback"
      @closeCallback="closeModalCallback"
    />
  </v-dialog>
</template>

<script>
import {mapActions} from "vuex";
import PrefectureList from "../../../../../mixins/PrefectureList"
import ZipCodeSearch from "../../../../../components/ZipCodeSearch"
import SearchZipModal from "../../../../components/SearchZipModal.vue";

export default {
  name: 'BillingAddressDialog',
  components: {
    ZipCodeSearch,
    SearchZipModal
  },
  mixins: [
    PrefectureList
  ],
  props: {
    enableDialog: {
      type: Boolean,
      default: () => false,
    },
    emission: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      enableEmissionDialog: false,
      enableTraderDialog: false,
      enableSearchZipModal: false,
      billing_address: null,
      /* テナント */
      tenants: [],
      /* 締め日 */
      closeDayTypes: [],
      /* 支払サイト */
      paymentSites: [],
      /* 入金日 */
      paymentDayTypes: [],
      /* 入金方法 */
      paymentMethodTypes: [],
      /* 相殺区分 */
      counterbalanceTypes: [],
      /* 端数処理区分 */
      fractionTypes: [],
      tableHeaders: [
        { value: 'name', text: '事業者名' },
        { value: 'subscriber_number', text: '加入者番号' },
        { value: 'created_at', text: '登録日時' },
        { value: 'editable', text: '削除', align: 'end' },
      ],
    }
  },
  computed: {
  },
  watch: {
    enableDialog (newVal) {
      if (newVal) {
        // Validationを初期化
        this.$nextTick(() => this.$refs.observer.reset())
        this.setDialogForm()
      }
    },
  },
  created() {
  },
  methods: {
    ...mapActions('DriverMasterBillingAddress', {
      newBillingAddress: 'new',
      createBillingAddress: 'create',
    }),
    ...mapActions('DriverMasterSearchTenant', {
      fetchTenants: 'index',
    }),
    searchZipButtonClickEvent () {
      this.enableSearchZipModal = true;
    },
    /**
     * 排出事業者情報をコピーボタン クリックイベント
     */
    copyFromEmissionButtonClickEvent () {
      this.billing_address.name = this.emission.name
      this.billing_address.zip_code = this.emission.zip_code
      this.billing_address.prefecture_code = this.emission.prefecture_code
      this.billing_address.address_1 = this.emission.address_1
      this.billing_address.address_2 = this.emission.address_2
      this.billing_address.address_3 = this.emission.address_3
      this.billing_address.phone_number = this.emission.phone_number
    },

    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent () {
      this.$emit('closeEvent')
    },
    /**
     * 登録するボタン クリックイベント
     */
    registerButtonClickEvent () {
      this.$refs.observer.validate().then((result) => {
        if(result){
          this.sendRegistrationInformation()
        }
      })
    },
    /**
     * 郵便番号検索 コールバック
     * @param content
     */
    setZipAddress (content) {
      this.billing_address.prefecture_code = `${content.pref_code}`
      this.billing_address.address_1 = `${content.locality}`
      this.billing_address.address_2 = `${content.street}`
    },
    setDialogForm() {
      this.fetchTenants().then((content) => {
        this.tenants = content.tenants
      })
      this.newBillingAddress().then((content) => {
        this.billing_address = content
        this.closeDayTypes = content.close_day_types
        this.paymentSites = content.payment_sites
        this.paymentDayTypes = content.payment_day_types
        this.paymentMethodTypes = content.payment_method_types
        this.counterbalanceTypes = content.counterbalance_types
        this.fractionTypes = content.fraction_types
      })
    },
    /**
     * 登録処理
     * 正常に登録された場合は発行された請求先IDを呼出元のメソッドへ渡す.
     */
    sendRegistrationInformation () {
      this.createBillingAddress({ params: this.billing_address }).then((content) => {
        this.$emit('registerEvent', content)
      })
    },
    closeModalCallback () {
      this.enableSearchZipModal = false;
    },
    /**
     * 郵便番号検索反映ボタン コールバック
     * @param address { Object }
     * @property address.zip_code { string } 郵便番号
     * @property address.prefecture_code { number } 都道府県コード
     * @property address.city { string } 市区町村
     * @property address.town { string } 町域
     */
    searchZipReflectCallback (address) {
      this.billing_address.zip_code = address.zip_code
      this.billing_address.prefecture_code = `${address.prefecture_code}`
      this.billing_address.address_1 = address.city
      this.billing_address.address_2 = address.town
    },
    searchZipReflectZipCallback (zipCode) {
      this.billing_address.zip_code = zipCode;
    },
  }
}
</script>
